import type { Ctx } from "@milkdown/ctx";
import type { Node as ProseNode } from "@milkdown/prose/model";
import type { EditorView, NodeView } from "@milkdown/prose/view";
import type { Processor } from "unified";
import { InputRule } from "@milkdown/prose/inputrules";
import { TextSelection } from "@milkdown/prose/state";
import {
  $command,
  $ctx,
  $inputRule,
  $nodeSchema,
  $remark,
  $view,
} from "@milkdown/utils";
import { createRoot, type Root } from "react-dom/client";
import { StrictMode } from "react";
import { VariableChip } from "./VariableChip";
import { editorIdCtx } from "./variablePlugin";
import {
  splitTemplateTokens,
  templateTokenToMarkdown,
  type TemplateTokenMdastNode,
} from "./templateTokens";
import { formatTemplateToken } from "@/components/SpotflowV2/Sidebar/variableRefs";

// Playbook template tokens ({{loop_item}}) are substituted by the playbook
// executor at run time, NOT by the engine — the node exists only inside the
// editor and always serializes back to the literal `{{name}}` text.
// Opt-in per editor via this ctx flag: most markdown fields have no template
// layer and user-typed braces must stay plain text there.
export const templateTokensEnabledCtx = $ctx(false, "templateTokensEnabled");

// The stringify handler is registered unconditionally (it only fires for
// template nodes, which gated editors never create); the parse-side text
// scan is what the flag controls.
export const templateTokensRemark = $remark(
  "templateTokens",
  (ctx: Ctx) =>
    function templateTokens(this: Processor) {
      this.data("toMarkdownExtensions", [
        ...(this.data("toMarkdownExtensions") ?? []),
        templateTokenToMarkdown,
      ]);
      return (tree: unknown) => {
        if (!ctx.get(templateTokensEnabledCtx.key)) return;
        splitTemplateTokens(tree);
      };
    }
);

export const templateSchema = $nodeSchema("template", () => ({
  inline: true,
  group: "inline",
  atom: true,
  selectable: true,
  draggable: false,
  marks: "",
  attrs: {
    name: { default: "" },
  },
  parseDOM: [
    {
      tag: "span[data-template-name]",
      getAttrs: (dom: HTMLElement | string) => {
        if (typeof dom === "string") return false;
        return { name: dom.getAttribute("data-template-name") ?? "" };
      },
    },
  ],
  toDOM: (node: ProseNode) => [
    "span",
    {
      "data-template-name": node.attrs.name as string,
      class: "variable-chip",
    },
  ],
  leafText: (node: ProseNode) => formatTemplateToken(node.attrs.name as string),
  parseMarkdown: {
    match: (node) => node.type === "templateToken",
    runner: (state, node, type) => {
      const value = (node as unknown as TemplateTokenMdastNode).value;
      state.addNode(type, { name: value ?? "" });
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === "template",
    runner: (state, node) => {
      state.addNode("templateToken", undefined, node.attrs.name as string);
    },
  },
}));

// Same shell as VariableNodeView: the chip resolves its display through the
// editor's inputs store, so an in-scope token renders the standard DataChip
// (source "Playbook") and an out-of-scope one falls back to the not-found
// chip. The shared `variable-chip` class keeps selection styling and the
// drag-ghost plugin working for both node types.
class TemplateNodeView implements NodeView {
  dom: HTMLElement;
  private root: Root;
  private editorId: string;
  private currentName: string;

  constructor(node: ProseNode, editorId: string) {
    this.editorId = editorId;
    this.currentName = (node.attrs.name as string) ?? "";
    this.dom = document.createElement("span");
    this.dom.setAttribute("data-template-name", this.currentName);
    this.dom.className = "variable-chip";
    this.dom.contentEditable = "false";
    this.root = createRoot(this.dom);
    this.renderChip();
  }

  private renderChip() {
    this.root.render(
      <StrictMode>
        <VariableChip
          editorId={this.editorId}
          inputId={formatTemplateToken(this.currentName)}
        />
      </StrictMode>
    );
  }

  update(node: ProseNode): boolean {
    if (node.type.name !== "template") return false;
    const nextName = (node.attrs.name as string) ?? "";
    if (nextName !== this.currentName) {
      this.currentName = nextName;
      this.dom.setAttribute("data-template-name", nextName);
      this.renderChip();
    }
    return true;
  }

  selectNode() {
    this.dom.classList.add("variable-chip-selected");
  }

  deselectNode() {
    this.dom.classList.remove("variable-chip-selected");
  }

  destroy() {
    queueMicrotask(() => this.root.unmount());
  }

  ignoreMutation() {
    return true;
  }
}

export const templateView = $view(
  templateSchema.node,
  (ctx: Ctx) => (node: ProseNode, _view: EditorView) =>
    new TemplateNodeView(node, ctx.get(editorIdCtx.key))
);

// Completes a hand-typed `{{name}}` into a chip the moment the closing brace
// lands — the token syntax is documented and users type it directly.
export const templateInputRule = $inputRule(
  (ctx) =>
    new InputRule(/\{\{([^{}\n]+)\}\}$/, (state, match, start, end) => {
      if (!ctx.get(templateTokensEnabledCtx.key)) return null;
      const name = match[1];
      if (!name) return null;
      const chip = templateSchema.type(ctx).create({ name });
      return state.tr.replaceWith(start, end, chip);
    })
);

export const insertTemplateCommand = $command(
  "InsertTemplate",
  (ctx) => (payload?: { name: string }) => (state, dispatch) => {
    const name = payload?.name ?? "";
    if (!name) return false;
    if (!dispatch) return true;
    const chip = templateSchema.type(ctx).create({ name });
    const space = state.schema.text(" ");
    let tr = state.tr.replaceSelectionWith(chip);
    const afterChip = tr.selection.from;
    tr = tr.insert(afterChip, space);
    const cursorPos = afterChip + space.nodeSize;
    tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
    dispatch(tr.scrollIntoView());
    return true;
  }
);

export const templatePlugin = [
  templateTokensEnabledCtx,
  templateTokensRemark,
  templateSchema,
  templateView,
  templateInputRule,
  insertTemplateCommand,
].flat();
